<script setup>
import { useToast } from 'primevue/usetoast';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { sessionCreateSchema } from '@schemas/sessionCreate';
import { createSession, getSession } from '@services/sessionService';
import { useSessionStore } from '@stores';
import TagsInput from './TagsInput.vue';
import { useDurationFormatInput } from '@utils';

const props = defineProps({
    editForm: {
        type: Boolean,
    },
    session_id: {
        type: [String, Number],
        default: null,
    },
});

const emit = defineEmits(['hide']);

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const sessionStore = useSessionStore();

const actionsShow = ref(false);
const initialData = ref({});

const settingsData = ref({
    session_id: '',
    session_name: null,
    video_url: '',
    token_url: null,
    auth: null,
    duration: null,
    base_score: null,
    locale: null,
    redirect_url: null,
    iframe_url: null,
    is_for_mobile: false,
    instruction: {
        url: null,
        name: null,
    },
    denied_list: [],
    test_id: null,
});

const settingsForm = ref(null);
const langs = [
    { name: 'Ўзбекча', code: 'uz' },
    { name: "O'zbekcha", code: 'la' },
    { name: 'English', code: 'en' },
    { name: 'Русский', code: 'ru' },
];
const selectedLangs = computed(() => {
    const test = settingsData.value.tests.find((t) => t.id === settingsData.value.test_id);
    return test ? langs.filter((l) => test.languages.includes(l.code)) : langs;
});

const privateSession = ref(true);

const resolver = ref(zodResolver(sessionCreateSchema(t, privateSession)));

const numberWriting = (event) => {
    const isNumber = /^\d+$/.test(event.key);

    const allowedKeys = ['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete'];

    if (allowedKeys.includes(event.key)) {
        return;
    }

    if (!isNumber) {
        event.preventDefault();
    }
};

const checkForm = async () => {
    return await settingsForm.value?.validate();
};

const resetForm = async () => {
    settingsData.value = JSON.parse(initialData.value);
    const { auth, token_url } = settingsData.value;
    privateSession.value = !!auth && !!token_url;

    await settingsForm.value?.reset();
};

const checkAdditionalSettings = () => {
    const { cheatings } = settingsData.value;

    return cheatings.every((cheating) => typeof cheating.score == 'number');
};

const submitSessionForm = async () => {
    const { values, errors } = await checkForm();
    const isAdditionalSettingsValid = checkAdditionalSettings();

    if (values && isAdditionalSettingsValid) {
        const sendingData = {
            ...settingsData.value,
            base_score: +settingsData.value.base_score,
            auth: privateSession.value ? settingsData.value.auth : null,
            token_url: privateSession.value ? settingsData.value.token_url : null,
        };
        try {
            const { data } = await createSession(sendingData);
            toast.add({ severity: 'success', summary: props.editForm ? t('form.changes_saved') : t('create_session.session_created'), life: 3000 });
            await sessionStore.getSessions();
            emit('hide');
        } catch (error) {
            console.error(error);
        }
    } else {
        toast.add({ severity: 'error', summary: t('form.please_check_fields'), life: 3000 });
    }
};

const codeChanged = (data) => {
    const { cheatings } = settingsData.value;

    const updateCheck = (code, check) => {
        const cheating = cheatings.find((c) => c.code === code);
        if (cheating) {
            cheating.check = check;
        }
    };

    if (data.code === 'b2' && !settingsData.value.denied_list?.length) {
        toast.add({ severity: 'info', summary: t('create_session.errors.b2Control'), life: 3000 });
        updateCheck('b2', false);
        return;
    } else {
        updateCheck('b2', true);
    }

    const linkedCodes = {
        p1: ['s2', 'b2'],
        s2: ['p1'],
        b2: ['p1'],
        m1: ['m2'],
        m2: ['m1'],
    };

    const { code, check } = data;

    if (linkedCodes[code]) {
        linkedCodes[code].forEach((linkedCode) => {
            if (code === 'p1' && !check) {
                updateCheck(linkedCode, false);
            } else if (['s2', 'b2'].includes(code) && check) {
                updateCheck('p1', true);
            } else if (code === 'm1' || code === 'm2') {
                updateCheck(linkedCode, check);
            }
        });
    }
};

const onSelectedFiles = async (event) => {
    const lastFile = event.files.slice(-1)[0];
    const { id, name, extension } = await sessionStore.uploadInstructionFile(lastFile);

    if (!settingsData.value.instruction) {
        settingsData.value.instruction = {};
    }
    settingsData.value.instruction.url = id;
    settingsData.value.instruction.name = `${name}.${extension}`;
};

const clearFile = (clearCallback) => {
    clearCallback();
    settingsData.value.instruction = null;
};

const handleTestAttach = (event, $form) => {
    if (event.value) {
        $form.iframe_url.value = 'https://app.proctoring.uz/testing';
        settingsData.value.iframe_url = 'https://app.proctoring.uz/testing';
        const test = settingsData.value.tests.find((t) => t.id === event.value);
        settingsData.value.duration = test.duration;
    } else {
        $form.iframe_url.value = null;
        settingsData.value.iframe_url = null;
        settingsData.value.duration = '01:00:00';
    }
};

const testsLink = computed(() => [{ id: null, isHeader: true }, ...settingsData.value.tests]);

const durationHandler = (e) => {
    if (settingsData.value.test_id) return;
    settingsData.value.duration = useDurationFormatInput(e.target.value);
};

try {
    const {
        data: { data },
    } = await getSession(props.editForm ? props.session_id : undefined);

    settingsData.value = data;
    initialData.value = JSON.stringify(data);

    const { auth, token_url } = data;

    privateSession.value = !!auth && !!token_url;

    actionsShow.value = true;
} catch (error) {
    console.error(error);
}

watch(
    () => settingsData.value.denied_list,
    (newList) => {
        if (!Array.isArray(newList)) {
            settingsData.value.denied_list = [];
        }
        codeChanged({ code: 'b2', check: settingsData.value.denied_list.length > 0 });
    },
    { deep: true }
);
</script>

<template>
    <Dialog modal dismissableMask :header="$t(`create_session.${editForm ? 'edit_session' : 'create_session'}`)" :style="{ width: '70vw' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
        <div class="create-session-form">
            <div class="form__inner-togglers">
                <div class="form__inner-toggler">
                    <ToggleSwitch v-model="privateSession" input-id="private__page" />
                    <label for="private__page"> {{ $t('create_session.private_page') }} </label>
                </div>
                <div class="form__inner-toggler">
                    <ToggleSwitch v-model="settingsData.is_for_mobile" input-id="for_mobile" />
                    <label for="for_mobile">{{ t('create_session.for_mobile') }}</label>
                </div>
            </div>

            <Form v-slot="$form" :initialValues="settingsData" :resolver="resolver" class="create-session-form__top" ref="settingsForm">
                <div class="create-session__form__field" v-if="privateSession">
                    <div class="form__inner-field url-for-token">
                        <label for="token_url" class="input-label required">{{ $t('create_session.url_to_get_token') }}</label>
                        <InputText name="token_url" type="text" placeholder="" v-model.trim="settingsData.token_url" />
                        <Message v-if="$form.token_url?.invalid" severity="error" size="small" variant="simple">
                            {{ $form.token_url.error?.message }}
                        </Message>
                    </div>
                    <div class="form__inner-field auth-urls-field">
                        <label for="auth" class="input-label required">{{ $t('create_session.authorization_token_for_urls') }}</label>
                        <InputText name="auth" type="text" placeholder="" v-model.trim="settingsData.auth" />
                        <Message v-if="$form.auth?.invalid" severity="error" size="small" variant="simple">
                            {{ $form.auth.error?.message }}
                        </Message>
                    </div>
                </div>

                <div class="create-session__form__field-wrapper">
                    <div class="form__inner-field">
                        <label for="test" class="input-label">{{ t('form.attach_test') }}</label>
                        <Select name="test" v-model="settingsData.test_id" showClear optionLabel="name" optionValue="id" filter :options="testsLink" @change="(e) => handleTestAttach(e, $form)" :placeholder="t('form.attach_test')">
                            <template #option="slotProps">
                                <template v-if="slotProps.option.isHeader">
                                    <Button as="a" href="/tests" target="_blank" icon="pi pi-plus" :label="t('tests.create')" fluid />
                                </template>
                                <template v-else>
                                    <div>{{ slotProps.option.name }}</div>
                                </template>
                            </template>
                        </Select>
                    </div>

                    <div class="form__inner-field">
                        <label for="iframe_url" class="input-label required">{{ $t('create_session.iframe_url') }}</label>
                        <InputText name="iframe_url" type="text" placeholder="" v-model.trim="settingsData.iframe_url" :disabled="!!settingsData.test_id" />
                        <Message v-if="$form.iframe_url?.invalid" severity="error" size="small" variant="simple">
                            {{ $form.iframe_url.error?.message }}
                        </Message>
                    </div>

                    <div class="form__inner-field">
                        <label for="redirect_url" class="input-label required">{{ $t('create_session.redirect_url') }}</label>
                        <InputText name="redirect_url" type="text" placeholder="" v-model.trim="settingsData.redirect_url" :disabled="!!settingsData.test_id" />
                        <Message v-if="$form.redirect_url?.invalid" severity="error" size="small" variant="simple">
                            {{ $form.redirect_url.error?.message }}
                        </Message>
                    </div>
                </div>

                <div class="create-session__form__field">
                    <div class="form__inner-field grow">
                        <label for="session_id" class="input-label required">UUID</label>
                        <InputText name="session_id" type="text" placeholder="" disabled />
                    </div>
                    <div class="form__inner-field grow">
                        <label for="session_name" class="input-label required">{{ $t('create_session.session_name') }}</label>
                        <InputText name="session_name" type="text" placeholder="" v-model.trim="settingsData.session_name" />
                        <Message v-if="$form.session_name?.invalid" severity="error" size="small" variant="simple">
                            {{ $form.session_name.error?.message }}
                        </Message>
                    </div>
                    <div class="form__inner-field grow">
                        <label for="video_url" class="input-label required">{{ $t('create_session.video_url') }}</label>
                        <InputText name="video_url" type="text" placeholder="" v-model.trim="settingsData.video_url" />
                        <Message v-if="$form.video_url?.invalid" severity="error" size="small" variant="simple">
                            {{ $form.video_url.error?.message }}
                        </Message>
                    </div>
                </div>

                <div class="create-session__form__field-grid">
                    <div class="form__inner-field">
                        <label for="duration" class="input-label required">{{ t('global.duration') }}</label>
                        <InputText name="duration" type="text" @input="durationHandler" placeholder="HH:MM:SS" inputmode="numeric" :value="settingsData.duration" :disabled="!!settingsData.test_id" />
                        <Message v-if="$form.duration?.invalid" severity="error" size="small" variant="simple">
                            {{ $form.duration.error?.message }}
                        </Message>
                    </div>

                    <div class="form__inner-field">
                        <label for="base_score" class="input-label required">{{ $t('create_session.confidence_level') }}</label>
                        <InputText name="base_score" type="text" placeholder="" @keydown="numberWriting" v-model="settingsData.base_score" />
                        <Message v-if="$form.base_score?.invalid" severity="error" size="small" variant="simple">
                            {{ $form.base_score.error?.message }}
                        </Message>
                    </div>

                    <div class="form__inner-field">
                        <label for="locale" class="input-label required">{{ $t('create_session.select_language') }}</label>
                        <Select name="locale" v-model="settingsData.locale" :options="selectedLangs" optionLabel="name" optionValue="code" :placeholder="$t('form.select_lang')" class="" checkmark />
                        <Message v-if="$form.locale?.invalid" severity="error" size="small" variant="simple">
                            {{ $form.locale.error?.message }}
                        </Message>
                    </div>

                    <div class="form__inner-field">
                        <label for="base_score" class="input-label">{{ $t('create_session.instruction_file') }}</label>

                        <FileUpload accept=".pdf,.doc,.docx" @select="onSelectedFiles">
                            <template #content>
                                {{ settingsData.instruction?.name }}
                            </template>
                            <template #header="{ chooseCallback, clearCallback }">
                                <div class="flex flex-wrap justify-between items-center flex-1 gap-4">
                                    <div class="flex gap-2">
                                        <Button @click="chooseCallback()" icon="pi pi-cloud-upload " rounded outlined severity="secondary" />
                                        <Button as="a" :href="settingsData.instruction?.url && `https://proctoring-api.univora.uz/services/platon-core/uploads/files/${settingsData.instruction?.url}`" icon="pi pi pi-download" rounded outlined severity="success" v-if="settingsData.instruction?.name && props.editForm" />
                                        <Button @click="clearFile(clearCallback)" icon="pi pi-times" rounded outlined severity="danger" v-if="settingsData.instruction?.name" />
                                    </div>
                                </div>
                            </template>
                        </FileUpload>
                    </div>
                    <div class="form__inner-field">
                        <label for="base_score" class="input-label">{{ $t('create_session.denied_list') }}</label>
                        <TagsInput v-model="settingsData.denied_list" />
                    </div>
                </div>
            </Form>
            <Accordion lazy :value="null">
                <AccordionPanel>
                    <AccordionHeader>{{ $t('create_session.additional_settings') }}</AccordionHeader>
                    <AccordionContent>
                        <div class="additional-settings">
                            <DataTable :value="settingsData.cheatings" tableStyle="">
                                <Column field="name" :header="$t('create_session.setting_description')">
                                    <template #body="slotProps">
                                        {{ $t(`violations.${slotProps.data.code}.short`) }}
                                    </template>
                                </Column>
                                <Column field="code" :header="$t('create_session.code')">
                                    <template #body="slotProps">
                                        {{ slotProps.data.code }}
                                    </template>
                                </Column>
                                <Column field="on_off" :header="$t('create_session.control')">
                                    <template #body="slotProps">
                                        <ToggleSwitch v-model="slotProps.data.check" @change="codeChanged(slotProps.data)" :disabled="slotProps.data.code === 'c1' || (slotProps.data.code === 'b2' && !settingsData.denied_list.length)" />
                                    </template>
                                </Column>
                                <Column field="score" :header="$t('create_session.deducted_score')">
                                    <template #body="slotProps">
                                        <InputText name="score" type="text" placeholder="" v-model.number="slotProps.data.score" @keydown="numberWriting" @contextmenu.prevent="" />
                                        <Message v-if="typeof slotProps.data.score == 'string'" severity="error" size="small" variant="simple">
                                            {{ $t('form.required_field') }}
                                        </Message>
                                    </template>
                                </Column>
                            </DataTable>
                        </div>
                    </AccordionContent>
                </AccordionPanel>
            </Accordion>
        </div>

        <template #footer>
            <div class="dialog-footer-actions" v-if="actionsShow">
                <Button type="reset" severity="secondary" :label="$t('form.reset')" @click="resetForm" class="self-end" />
                <Button type="submit" severity="secondary" :label="editForm ? $t('form.save_changes') : $t('create_session.create_session')" @click="submitSessionForm" class="self-end" />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
.create-session-form {
    @apply flex flex-col gap-y-4;
}

.create-session-form__top {
    @apply flex flex-col gap-y-4;
}

.create-session__form__field {
    @apply flex gap-4 flex-wrap;
}

.create-session__form__field-grid {
    @apply grid gap-4;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
}

.create-session__form__field-wrapper {
    @apply grid grid-cols-3 gap-4;
}

.form__inner-field {
    @apply flex flex-col gap-y-2;
}

.url-for-token {
    flex-grow: 2;
}

.auth-urls-field {
    flex-grow: 1;
}

.form__inner-togglers {
    @apply flex items-center gap-4;
}

.form__inner-toggler {
    @apply flex items-center gap-2;
}

.duration-as-hour-minute {
    @apply flex items-center justify-center h-[42px] max-h-[42px];
}

.dialog-footer-actions {
    @apply flex items-center gap-x-3;
}

.form__inner-field .p-fileupload {
    @apply flex gap-2 items-center flex-row-reverse justify-end;
}

:deep.form__inner-field .p-fileupload {
    @apply p-2;
}
:deep.form__inner-field .p-fileupload-header,
:deep.form__inner-field .p-fileupload-content {
    @apply p-0;
}

:deep.form__inner-field .p-fileupload-content {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
}

label.input-label.required::after {
    content: '*';
    color: red;
}

@media only screen and (max-width: 768px) {
    .create-session__form__field {
        @apply flex-col;
    }
    .create-session__form__field-wrapper {
        @apply grid-cols-1;
    }
}
</style>
